import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { PriceRef } from '@/components/PriceRef';
import { Users, TrendingUp, Award, Zap, Eye, EyeOff, Mail, Headphones, BookOpen, Sparkles, ChevronRight, Wand2, Copy, Check, Mic, Video, Calendar, Percent, Share2, Play, ArrowRight, ArrowLeft, Globe, Rocket, Gift, Star, DollarSign, BookMarked, Tv, Users2, MessageCircle, ChevronLeft, Lock } from 'lucide-react';
import axios from 'axios';
import Footer from '@/components/Footer';
import VideoCarousel from '@/components/VideoCarousel';
import LatestContentCarousel from '@/components/LatestContentCarousel';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '@/components/LanguageSelector';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// ============================================
// SCROLL ANIMATION HOOK
// ============================================
const useScrollAnimation = (options = {}) => {
  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, stop observing (one-time animation)
          observer.unobserve(element);
        }
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || '0px 0px -50px 0px'
      }
    );
    
    observer.observe(element);
    
    return () => {
      if (element) observer.unobserve(element);
    };
  }, [options.threshold, options.rootMargin]);
  
  return [elementRef, isVisible];
};

// Componente wrapper para animaciones de scroll
const AnimatedSection = ({ children, className = '', animation = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation();
  
  const delayClass = delay > 0 ? `delay-${delay}` : '';
  const animationClass = animation || '';
  
  return (
    <div 
      ref={ref} 
      className={`animate-on-scroll ${animationClass} ${delayClass} ${isVisible ? 'is-visible' : ''} ${className}`}
    >
      {children}
    </div>
  );
};

// Videos de YouTube para cada sección
const YOUTUBE_VIDEOS = {
  audiolector: [
    { id: '1-89dtNCDtM', title: 'Bienvenido a ViubuxConnect' },
    { id: '9JHiJpXFbzQ', title: 'Cómo funciona' },
    { id: 'fFAGyO9DqV0', title: 'Testimonios' },
    { id: 'A_es2rAXMjs', title: 'Beneficios' },
    { id: 'BWTSMWmKu4c', title: 'Comisiones' },
    { id: 'dha0J_TSMCI', title: 'Comunidad' },
    { id: '9fVhu7N40Z0', title: 'Catálogo' },
    { id: 'cJYkMzagx8s', title: 'Experiencias' },
    { id: 'Kym1OoaHA_I', title: 'Únete' },
  ],
  autor: [
    { id: 'NZoXW_wFpNw', title: 'Programa de Autores' },
    { id: 'w1_aQ6_TOvs', title: 'Beneficios Exclusivos' },
    { id: 'gfOh4r1CnB8', title: 'Producción Profesional' },
    { id: 'dbonLZaWpCo', title: 'Testimonios de Autores' },
  ]
};

// Componente de Carrusel de YouTube
const YouTubeCarousel = ({ videos, theme = 'cyan' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const themeColors = {
    cyan: {
      border: 'border-cyan-500/30',
      bg: 'bg-cyan-500/10',
      text: 'text-cyan-400',
      hover: 'hover:bg-cyan-500/20',
      dot: 'bg-cyan-500',
      dotInactive: 'bg-white/30'
    },
    violet: {
      border: 'border-violet-500/30',
      bg: 'bg-violet-500/10',
      text: 'text-violet-400',
      hover: 'hover:bg-violet-500/20',
      dot: 'bg-violet-500',
      dotInactive: 'bg-white/30'
    }
  };
  
  const colors = themeColors[theme];
  
  const nextVideo = () => {
    setCurrentIndex((prev) => (prev + 1) % videos.length);
    setIsPlaying(false);
  };
  
  const prevVideo = () => {
    setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
    setIsPlaying(false);
  };
  
  const goToVideo = (index) => {
    setCurrentIndex(index);
    setIsPlaying(false);
  };
  
  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Video Principal */}
      <div className={`relative rounded-2xl overflow-hidden ${colors.border} border backdrop-blur-sm`}>
        <div className="aspect-video w-full">
          {isPlaying ? (
            <iframe
              src={`https://www.youtube.com/embed/${videos[currentIndex].id}?autoplay=1&rel=0`}
              title={videos[currentIndex].title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div 
              className="relative w-full h-full cursor-pointer group"
              onClick={() => setIsPlaying(true)}
            >
              <img
                src={`https://img.youtube.com/vi/${videos[currentIndex].id}/maxresdefault.jpg`}
                alt={videos[currentIndex].title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = `https://img.youtube.com/vi/${videos[currentIndex].id}/hqdefault.jpg`;
                }}
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <div className={`w-20 h-20 rounded-full ${colors.bg} ${colors.border} border-2 flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <Play className={`w-10 h-10 ${colors.text} ml-1`} fill="currentColor" />
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Controles de navegación */}
        <button
          onClick={prevVideo}
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 ${colors.hover} flex items-center justify-center transition-all z-10`}
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={nextVideo}
          className={`absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 ${colors.hover} flex items-center justify-center transition-all z-10`}
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>
      
      {/* Título del video */}
      <p className="text-center text-white/70 mt-4 text-sm">
        {videos[currentIndex].title}
      </p>
      
      {/* Indicadores de posición */}
      <div className="flex justify-center gap-2 mt-4">
        {videos.map((_, index) => (
          <button
            key={index}
            onClick={() => goToVideo(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentIndex ? colors.dot : colors.dotInactive
            } ${index === currentIndex ? 'w-6' : ''}`}
          />
        ))}
      </div>
      
      {/* Thumbnails (visible solo en desktop) */}
      <div className="hidden md:flex gap-3 mt-6 overflow-x-auto pb-2 scrollbar-hide">
        {videos.map((video, index) => (
          <button
            key={video.id}
            onClick={() => goToVideo(index)}
            className={`flex-shrink-0 w-32 rounded-lg overflow-hidden border-2 transition-all ${
              index === currentIndex 
                ? `${colors.border} ring-2 ring-offset-2 ring-offset-slate-950 ${theme === 'cyan' ? 'ring-cyan-500' : 'ring-violet-500'}` 
                : 'border-transparent opacity-60 hover:opacity-100'
            }`}
          >
            <img
              src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
              alt={video.title}
              className="w-full h-18 object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// Imágenes para las secciones
const IMAGES = {
  audioLector: {
    hero: 'https://images.unsplash.com/photo-1585236873828-5725311e7c06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxwZXJzb24lMjBsaXN0ZW5pbmclMjBhdWRpb2Jvb2slMjBoZWFkcGhvbmVzJTIwcmVhZGluZ3xlbnwwfHx8fDE3ODA1NDY2NDF8MA&ixlib=rb-4.1.0&q=85',
    catalog: 'https://images.pexels.com/photos/6633272/pexels-photo-6633272.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  autor: {
    hero: '/autor-conferencia-hero.jpg',
    studio: 'https://images.unsplash.com/photo-1589903308904-1010c2294adc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxwb2RjYXN0JTIwcmVjb3JkaW5nJTIwc3R1ZGlvJTIwbWljcm9waG9uZSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODA1NDY2NDF8MA&ixlib=rb-4.1.0&q=85',
    event: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxsaXZlJTIwZXZlbnQlMjBjb25mZXJlbmNlJTIwc3RhZ2UlMjBhdWRpZW5jZXxlbnwwfHx8fDE3ODA1NDY2NDF8MA&ixlib=rb-4.1.0&q=85',
    microphone: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxwb2RjYXN0JTIwcmVjb3JkaW5nJTIwc3R1ZGlvJTIwbWljcm9waG9uZSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODA1NDY2NDF8MA&ixlib=rb-4.1.0&q=85',
  }
};

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showAuth, setShowAuth] = useState(false);
  const [authConfig, setAuthConfig] = useState({ showRegister: false, userType: 'consumer' });
  const [selectedPath, setSelectedPath] = useState(null); // null, 'audiolector', 'autor'
  const referralCode = searchParams.get('ref');
  const noDiscount = searchParams.get('nd') === '1'; // Si nd=1, no aplicar descuento

  // Helper functions para cookies cross-domain
  const setCrossDomainCookie = (name, value, days = 30) => {
    const maxAge = days * 24 * 60 * 60;
    // Domain=.viubux.com permite que play.viubux.com lea la cookie
    // En desarrollo/preview usamos el dominio actual
    const isProduction = window.location.hostname.includes('viubux.com');
    const domain = isProduction ? '; Domain=.viubux.com' : '';
    document.cookie = `${name}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax; Secure${domain}`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  const deleteCookie = (name) => {
    const isProduction = window.location.hostname.includes('viubux.com');
    const domain = isProduction ? '; Domain=.viubux.com' : '';
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; Path=/${domain}`;
  };

  // Guardar el código de referido cuando el usuario llega con ?ref=
  // La cookie expira en 90 días y es accesible desde todos los subdominios de viubux.com
  useEffect(() => {
    if (referralCode) {
      // Emitir cookie cross-domain real vía cabecera Set-Cookie desde Connect
      // (Domain=.viubux.com; Path=/; Max-Age=5184000; SameSite=Lax; Secure)
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      axios.get(`${BACKEND_URL}/api/referral/set-cookie`, {
        params: { ref: referralCode },
        withCredentials: true
      }).catch(() => {});

      // Respaldo cliente por si el fetch falla (mismo formato con Max-Age)
      setCrossDomainCookie('vbx_ref', referralCode, 90);
      setCrossDomainCookie('vbx_ref_ts', Date.now().toString(), 90);
      
      // También mantener en localStorage como respaldo local
      localStorage.setItem('viubux_referral', JSON.stringify({
        code: referralCode,
        timestamp: Date.now()
      }));
    }
  }, [referralCode]);

  // Obtener el código de referido válido (no expirado - 90 días)
  const getValidReferralCode = () => {
    // Si hay código en URL, usarlo directamente
    if (referralCode) return referralCode;
    
    // Verificar cookie primero (cross-domain)
    const cookieRef = getCookie('vbx_ref');
    const cookieTs = getCookie('vbx_ref_ts');
    
    if (cookieRef && cookieTs) {
      const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
      const isExpired = Date.now() - parseInt(cookieTs) > ninetyDaysMs;
      
      if (!isExpired) {
        return cookieRef;
      } else {
        // Limpiar cookies expiradas
        deleteCookie('vbx_ref');
        deleteCookie('vbx_ref_ts');
      }
    }
    
    // Fallback a localStorage
    const storedReferral = localStorage.getItem('viubux_referral');
    if (storedReferral) {
      const parsed = JSON.parse(storedReferral);
      const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
      const isExpired = Date.now() - parsed.timestamp > ninetyDaysMs;
      
      if (isExpired) {
        localStorage.removeItem('viubux_referral');
        return '';
      }
      return parsed.code;
    }
    return '';
  };

  const validReferralCode = getValidReferralCode();

  // Abrir modal de login si viene de /login (con ?login=true)
  useEffect(() => {
    const loginParam = searchParams.get('login');
    if (loginParam === 'true' && !user) {
      setShowAuth(true);
      setAuthConfig({ showRegister: false, userType: 'consumer' });
    }
  }, [searchParams, user]);

  // Redirect to dashboard if user is logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Función para abrir el modal de auth
  const openAuthModal = (showRegister = false, userType = 'consumer') => {
    setAuthConfig({ showRegister, userType });
    setShowAuth(true);
  };

  // Show loading or nothing while redirecting
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-3 border-violet-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  // Renderizar contenido según el camino seleccionado
  const renderPathContent = () => {
    const handleBack = () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      setSelectedPath(null);
    };
    
    if (selectedPath === 'audiolector') {
      return <AudioLectorContent openAuthModal={openAuthModal} onBack={handleBack} />;
    }
    if (selectedPath === 'autor') {
      return <AutorContent openAuthModal={openAuthModal} onBack={handleBack} />;
    }
    if (selectedPath === 'free') {
      // Plan Gratis - abre directamente el modal de registro
      openAuthModal(true, 'free');
      setSelectedPath(null);
      return <PathSelector onSelectPath={setSelectedPath} openAuthModal={openAuthModal} />;
    }
    return <PathSelector onSelectPath={setSelectedPath} openAuthModal={openAuthModal} />;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header - Moderno y tecnológico */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 md:px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {selectedPath && (
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'instant' });
                  setSelectedPath(null);
                }}
                className="flex items-center gap-1 text-white/60 hover:text-white transition-colors mr-2"
                data-testid="back-to-home"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Inicio</span>
              </button>
            )}
            <img 
              src="/viubux_logo_web.png" 
              alt="Viubux Connect" 
              className="h-8 md:h-10 w-auto object-contain brightness-0 invert"
              style={{ maxWidth: '160px' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML += '<span class="gotham-bold text-xl text-white">ViubuxConnect</span>';
              }}
            />
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle className="mr-2" />
            <button
              data-testid="header-register-btn"
              onClick={() => openAuthModal(true, selectedPath === 'autor' ? 'author' : 'consumer')}
              className="hidden md:block text-sm font-medium text-white/70 hover:text-white transition-colors"
            >
              Registrarse
            </button>
            <Button
              data-testid="header-login-btn"
              onClick={() => openAuthModal(false)}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 shadow-lg shadow-violet-500/25"
            >
              Ingresar
            </Button>
          </div>
        </div>
      </header>

      {/* Espaciador para header fijo */}
      <div className="h-14 md:h-16"></div>
      
      {/* Contenido dinámico según el camino seleccionado */}
      {renderPathContent()}

      <Footer />

      {/* Auth Modal - Estilo app.viubux.com */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="auth-modal-overlay">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative shadow-2xl max-h-[90vh] overflow-y-auto">
            <button
              data-testid="close-auth-modal"
              onClick={() => setShowAuth(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <AuthModalContent 
              onClose={() => setShowAuth(false)} 
              initialReferralCode={validReferralCode || ''} 
              initialShowRegister={authConfig.showRegister}
              initialUserType={authConfig.userType}
              applyDiscount={!noDiscount}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// PATH SELECTOR - Pantalla inicial de elección
// ============================================
const PathSelector = ({ onSelectPath, openAuthModal }) => {
  // Wrapper function to scroll to top when selecting a path
  const handleSelectPath = (path) => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    onSelectPath(path);
  };
  
  return (
    <>
      {/* Hero Section - Selector de Camino */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background con efecto tecnológico */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-violet-950/50 to-slate-950"></div>
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
          {/* Glowing orbs - with float animation */}
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-[120px] animate-float"></div>
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[120px] animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[150px]"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Hero Text with staggered animations */}
          <div className="text-center mb-12">
            <p className="animate-hero-text verdana-regular text-xs md:text-sm text-white/60 mb-6 max-w-3xl mx-auto" data-testid="hero-welcome-text">
              <strong className="text-white/90">Bienvenidos a Viubux Connect</strong> — El sitio oficial para conectar con{' '}
              <a href="https://reader.viubux.com" target="_blank" rel="noreferrer" className="text-violet-300 hover:text-violet-200 underline underline-offset-2 transition-colors" data-testid="hero-reader-link">Viubux Reader</a>
              {' '}y{' '}
              <a href="https://play.viubux.com" target="_blank" rel="noreferrer" className="text-violet-300 hover:text-violet-200 underline underline-offset-2 transition-colors" data-testid="hero-play-link">Viubux Play</a>.
            </p>
            <h1 className="gotham-black text-4xl sm:text-5xl lg:text-6xl xl:text-7xl mb-6 text-white leading-tight">
              <span className="animate-hero-text delay-1 inline-block">Conectamos</span>
              <span className="block mt-2 bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-hero-text delay-2">
                Mentes Brillantes
              </span>
            </h1>
            <p className="animate-hero-text delay-3 verdana-regular text-lg md:text-xl text-white/60 mb-4 max-w-2xl mx-auto">
              El ecosistema donde autores y audiencias se encuentran para crear, aprender y crecer juntos.
            </p>
            <div className="animate-hero-text delay-3 flex flex-col items-center gap-3 mt-8">
              <p className="text-sm text-white/50 tracking-wide">Descubre:</p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <a href="https://play.viubux.com" target="_blank" rel="noreferrer" data-testid="hero-play-logo-btn"
                  className="group inline-flex items-center px-6 py-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/15 hover:border-violet-400/50 transition-all">
                  <img src="/vbx-play-ondark.png" alt="Viubux Play" className="h-11 md:h-12 w-auto object-contain group-hover:scale-105 transition-transform" onError={(e)=>{e.target.style.display='none';}} />
                </a>
                <a href="https://reader.viubux.com" target="_blank" rel="noreferrer" data-testid="hero-reader-logo-btn"
                  className="group inline-flex items-center px-6 py-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/15 hover:border-cyan-400/50 transition-all">
                  <img src="/vbx-reader-ondark.png" alt="Viubux Reader" className="h-11 md:h-12 w-auto object-contain group-hover:scale-105 transition-transform" onError={(e)=>{e.target.style.display='none';}} />
                </a>
              </div>
            </div>
          </div>

          {/* Pregunta principal */}
          <AnimatedSection className="text-center mb-10" animation="blur-in">
            <h2 className="gotham-bold text-2xl md:text-3xl text-white mb-2">
              ¿Cómo quieres conectar?
            </h2>
            <p className="text-white/50 text-sm mb-4">Elige tu camino y descubre los beneficios</p>
            <p className="text-white/70 text-sm max-w-3xl mx-auto bg-gradient-to-r from-violet-500/10 to-cyan-500/10 px-6 py-3 rounded-xl border border-white/10">
              Regístrate ahora al universo de Viubux y ten acceso a más de <strong className="text-cyan-400">250,000 contenidos</strong> entre audiolibros, podcasts (+20,000 episodios), ebooks, música, películas y contenidos audiovisuales disponibles en venta directa.
            </p>
          </AnimatedSection>

          {/* Cards de selección */}
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto md:items-start">
            {/* Card Plan Gratis - NUEVO */}
            <AnimatedSection animation="from-left" delay={0} className="md:mt-16">
              <button
                onClick={() => handleSelectPath('free')}
                className="w-full h-full group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-emerald-400/50 transition-all duration-500 text-left overflow-hidden hover:scale-[1.02] hover:shadow-2xl hover:shadow-emerald-500/20"
                data-testid="select-free-path"
              >
                {/* Badge GRATIS */}
                <div className="absolute -top-1 -right-1 bg-gradient-to-r from-emerald-600 to-green-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl rounded-tr-3xl shadow-lg">
                  GRATIS
                </div>
                
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 to-green-500/0 group-hover:from-emerald-500/10 group-hover:to-green-500/10 transition-all duration-500 rounded-3xl"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="gotham-bold text-2xl text-white mb-2">Plan Gratis</h3>
                  <p className="text-emerald-300 font-semibold text-lg mb-4">"Explora sin compromiso"</p>
                  
                  <ul className="space-y-3 mb-6 text-sm">
                    <li className="flex items-center gap-3 text-white/70">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>Contenido gratuito ilimitado</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>Acceso a Viubux Play</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>Acceso a Viubux Reader</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>Compra en Venta Directa</span>
                    </li>
                  </ul>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-2xl font-black text-emerald-400">$0</span>
                      <span className="text-white/50 text-xs ml-1">MXN/mes</span>
                    </div>
                    <div className="flex items-center gap-2 text-emerald-400 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                      Comenzar <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </button>
            </AnimatedSection>

            {/* Card Audio-Lector */}
            <AnimatedSection animation="scale-up" delay={1} className="md:mt-8">
              <button
                onClick={() => handleSelectPath('audiolector')}
                className="w-full group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-cyan-400/50 transition-all duration-500 text-left overflow-hidden hover:scale-[1.02] hover:shadow-2xl hover:shadow-cyan-500/20"
                data-testid="select-audiolector-path"
              >
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-blue-500/0 group-hover:from-cyan-500/10 group-hover:to-blue-500/10 transition-all duration-500 rounded-3xl"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                    <Headphones className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="gotham-bold text-2xl text-white mb-2">Soy Audio-Lector</h3>
                  <p className="text-cyan-300 font-semibold text-lg mb-4">"Escucha, lee y gana"</p>
                  
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-3 text-white/70">
                      <BookMarked className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span>Colección ViubuxConnect + 500 títulos</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <BookOpen className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span>Acceso a Viubux Reader</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <Headphones className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span>Acceso a Viubux Play</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <DollarSign className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span>25% comisión por referidos</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <Zap className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span>Ingresos recurrentes</span>
                    </li>
                  </ul>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-3xl font-black text-white">$199</span>
                      <span className="text-white/50 text-sm ml-1">MXN/mes</span>
                      <div><PriceRef mxn={199} /></div>
                    </div>
                    <div className="flex items-center gap-2 text-cyan-400 font-semibold group-hover:translate-x-2 transition-transform">
                      Explorar <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </button>
            </AnimatedSection>

            {/* Card Autor */}
            <AnimatedSection animation="from-right" delay={2}>
              <button
                onClick={() => handleSelectPath('autor')}
                className="w-full group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-violet-400/50 transition-all duration-500 text-left overflow-hidden hover:scale-[1.02] hover:shadow-2xl hover:shadow-violet-500/20"
                data-testid="select-autor-path"
              >
                {/* Badge exclusivo */}
                <div className="absolute -top-1 -right-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl rounded-tr-3xl shadow-lg">
                  +11 BENEFICIOS
                </div>
                
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/0 to-purple-500/0 group-hover:from-violet-500/10 group-hover:to-purple-500/10 transition-all duration-500 rounded-3xl"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                    <BookOpen className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="gotham-bold text-2xl text-white mb-2">Soy Autor/Creador</h3>
                  <p className="text-violet-300 font-semibold text-lg mb-4">"Publica, crece y monetiza"</p>
                  
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-3 text-white/70">
                      <Mic className="w-5 h-5 text-violet-400 flex-shrink-0" />
                      <span>Distribuye hasta 5 obras</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <Rocket className="w-5 h-5 text-violet-400 flex-shrink-0" />
                      <span>Suite Pro-Marketing con IA</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <Star className="w-5 h-5 text-violet-400 flex-shrink-0" />
                      <span>Participación en Pool de Autores</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <Users className="w-5 h-5 text-violet-400 flex-shrink-0" />
                      <span>Programa de Afiliados exclusivo</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <Percent className="w-5 h-5 text-violet-400 flex-shrink-0" />
                      <span>15% Descuento en Producción A/V</span>
                    </li>
                    <li className="flex items-center gap-3 text-white/70">
                      <Video className="w-5 h-5 text-violet-400 flex-shrink-0" />
                      <span>1 Co-Producción al 50% anual</span>
                    </li>
                  </ul>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-3xl font-black text-white">$4,997</span>
                      <span className="text-white/50 text-sm ml-1">+ $199/mes</span>
                      <div><PriceRef mxn={4997} /></div>
                    </div>
                    <div className="flex items-center gap-2 text-violet-400 font-semibold group-hover:translate-x-2 transition-transform">
                      Explorar <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </button>
            </AnimatedSection>
          </div>

          {/* Link para login */}
          <AnimatedSection className="text-center mt-10" delay={3}>
            <p className="text-white/40">
              ¿Ya tienes cuenta?{' '}
              <button onClick={() => openAuthModal(false)} className="text-violet-400 hover:text-violet-300 underline transition-colors">
                Inicia sesión aquí
              </button>
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900/50 border-y border-white/5">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <AnimatedSection animation="scale-up" delay={1}>
              <p className="gotham-black text-4xl md:text-5xl text-white mb-2">+500</p>
              <p className="text-white/50 text-sm">Títulos exclusivos</p>
            </AnimatedSection>
            <AnimatedSection animation="scale-up" delay={2}>
              <p className="gotham-black text-4xl md:text-5xl text-white mb-2">25%</p>
              <p className="text-white/50 text-sm">Comisión directa</p>
            </AnimatedSection>
            <AnimatedSection animation="scale-up" delay={3}>
              <p className="gotham-black text-4xl md:text-5xl text-white mb-2">42%</p>
              <p className="text-white/50 text-sm">Pool de autores</p>
            </AnimatedSection>
            <AnimatedSection animation="scale-up" delay={4}>
              <p className="gotham-black text-4xl md:text-5xl text-white mb-2">15%</p>
              <p className="text-white/50 text-sm">Descuento servicios</p>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </>
  );
};

// ============================================
// AUDIO-LECTOR CONTENT - Contenido para Audio-Lectores
// ============================================
const AudioLectorContent = ({ openAuthModal, onBack }) => {
  return (
    <>
      {/* Hero Audio-Lector */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-cyan-950/30 to-slate-950"></div>
          <div className="absolute top-0 right-0 w-1/2 h-full">
            <img 
              src={IMAGES.audioLector.hero}
              alt="Audio-Lector"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950 to-transparent"></div>
          </div>
          <div className="absolute top-20 left-20 w-72 h-72 bg-cyan-500/20 rounded-full blur-[100px] animate-float"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Video de presentación Audio-Lector (Vimeo) */}
          <div className="max-w-3xl mx-auto mb-10">
            <div className="rounded-2xl overflow-hidden border border-cyan-500/20 shadow-2xl shadow-cyan-500/10 bg-black" data-testid="audiolector-video" style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
              <iframe
                src="https://player.vimeo.com/video/1229758822?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&loop=1"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Viubux Media te presenta su programa de Afiliados 4x4 - El poder de la Recomendación"
              />
            </div>
            <p className="text-center text-white/40 text-xs mt-3" data-testid="audiolector-video-disclaimer">*Comisiones sujetas a retenciones de impuestos.</p>
          </div>
          <div className="max-w-2xl">
            <div className="animate-hero-text inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-6">
              <Headphones className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium text-cyan-300">Plan Audio-Lector</span>
            </div>
            
            <h1 className="gotham-black text-4xl sm:text-5xl lg:text-6xl mb-6 text-white leading-tight">
              <span className="animate-hero-text delay-1 inline-block">Escucha, Lee</span>
              <span className="block text-cyan-400 animate-hero-text delay-2">y Gana</span>
            </h1>
            
            <p className="animate-hero-text delay-3 verdana-regular text-lg text-white/70 mb-8">
              Acceso ilimitado a la colección "ViubuxConnect" + 500 títulos (Texto, Audio y Video) en Viubux Play y Viubux Reader. 
              Comparte tu enlace de afiliado y gana <strong className="text-cyan-400">25% de comisión</strong> por cada nuevo suscriptor.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => openAuthModal(true, 'consumer')}
                className="animate-hero-text delay-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-lg px-8 py-6 rounded-full font-semibold shadow-lg shadow-cyan-500/25 transition-all duration-300"
                data-testid="audiolector-cta-main"
              >
                Comenzar por $199/mes
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios Audio-Lector */}
      <section className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <h2 className="gotham-bold text-3xl md:text-4xl text-white mb-4">
              Tus Beneficios como Audio-Lector
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Todo lo que necesitas para disfrutar y monetizar el contenido
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* Beneficio 1 */}
            <AnimatedSection delay={1}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-all group h-full">
                <div className="w-14 h-14 mb-5 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <BookMarked className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="gotham-bold text-lg mb-2 text-white">Acceso al Catálogo ViubuxConnect</h3>
                <p className="text-white/50 text-sm">Acceso ilimitado a la colección "ViubuxConnect" + 500 títulos (Texto, Audio y Video) en Viubux Play y Viubux Reader</p>
              </div>
            </AnimatedSection>

            {/* Beneficio 2 */}
            <AnimatedSection delay={2}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-all group h-full">
                <div className="w-14 h-14 mb-5 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <DollarSign className="w-7 h-7 text-green-400" />
                </div>
                <h3 className="gotham-bold text-lg mb-2 text-white">25% Comisión Directa</h3>
                <p className="text-white/50 text-sm">Gana $49.75 MXN por cada referido que se suscriba* (menos comisiones e impuestos)</p>
              </div>
            </AnimatedSection>

            {/* Beneficio 3 */}
            <AnimatedSection delay={3}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-all group h-full">
                <div className="w-14 h-14 mb-5 rounded-xl bg-gradient-to-br from-purple-500/20 to-violet-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Zap className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="gotham-bold text-lg mb-2 text-white">Ingresos Recurrentes</h3>
                <p className="text-white/50 text-sm">Gana mes con mes mientras tus referidos permanezcan activos</p>
              </div>
            </AnimatedSection>

            {/* Beneficio 4 */}
            <AnimatedSection delay={4}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-all group h-full">
                <div className="w-14 h-14 mb-5 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Share2 className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="gotham-bold text-lg mb-2 text-white">Link Personalizado</h3>
                <p className="text-white/50 text-sm">Tu propio enlace de afiliado para compartir en redes sociales</p>
              </div>
            </AnimatedSection>

            {/* Beneficio 5 - Logotipos Viubux Play y Viubux Reader */}
            <AnimatedSection delay={5}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-cyan-500/30 transition-all group h-full flex flex-col justify-center" data-testid="audiolector-benefit-logos">
                <h3 className="gotham-bold text-lg mb-4 text-white text-center">Disponible en</h3>
                <div className="flex flex-col items-center justify-center gap-4">
                  <img src="/vbx-play-ondark.png" alt="Viubux Play" className="h-9 w-auto object-contain group-hover:scale-105 transition-transform" onError={(e)=>{e.target.style.display='none';}} />
                  <img src="/vbx-reader-ondark.png" alt="Viubux Reader" className="h-9 w-auto object-contain group-hover:scale-105 transition-transform" onError={(e)=>{e.target.style.display='none';}} />
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection className="text-center mb-16">
              <h2 className="gotham-bold text-3xl md:text-4xl text-white mb-4">
                ¿Cómo funciona?
              </h2>
              <p className="text-white/50">Comienza en 3 simples pasos</p>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-8">
              <AnimatedSection animation="scale-up" delay={1} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-2xl font-black">
                  1
                </div>
                <h3 className="gotham-bold text-xl text-white mb-3">Suscríbete</h3>
                <p className="text-white/50">Paga $199 MXN/mes y accede al catálogo completo de ViubuxConnect</p>
              </AnimatedSection>
              <AnimatedSection animation="scale-up" delay={2} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-2xl font-black">
                  2
                </div>
                <h3 className="gotham-bold text-xl text-white mb-3">Comparte</h3>
                <p className="text-white/50">Usa tu link de afiliado en redes sociales, WhatsApp o donde prefieras</p>
              </AnimatedSection>
              <AnimatedSection animation="scale-up" delay={3} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-2xl font-black">
                  3
                </div>
                <h3 className="gotham-bold text-xl text-white mb-3">Gana</h3>
                <p className="text-white/50">Recibe 25% de comisión por cada suscriptor que llegue por tu enlace</p>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Sección de Videos YouTube - Audio-Lector */}
      <section className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-12">
            <h2 className="gotham-bold text-3xl md:text-4xl text-white mb-4">
              Conoce más sobre ViubuxConnect
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Descubre cómo otros audio-lectores están disfrutando y ganando con nuestra plataforma
            </p>
          </AnimatedSection>
          
          <AnimatedSection animation="scale-up">
            <YouTubeCarousel videos={YOUTUBE_VIDEOS.audiolector} theme="cyan" />
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Final Audio-Lector */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/10 blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="gotham-black text-3xl sm:text-4xl md:text-5xl text-white mb-6">
            ¡Comienza a escuchar y ganar hoy!
          </h2>
          <p className="verdana-regular text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Por solo $199 MXN al mes, accede a contenido premium y monetiza tu red de contactos
          </p>
          <Button
            onClick={() => openAuthModal(true, 'consumer')}
            className="bg-white text-cyan-600 hover:bg-gray-50 text-lg px-10 py-6 rounded-full font-bold shadow-xl hover:shadow-2xl transition-all duration-300"
            data-testid="audiolector-cta-final"
          >
            Unirme como Audio-Lector
          </Button>
        </div>
      </section>
    </>
  );
};

// ============================================
// AUTOR CONTENT - Contenido para Autores
// ============================================
const AutorContent = ({ openAuthModal, onBack }) => {
  return (
    <>
      {/* Hero Autor */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-violet-950/30 to-slate-950"></div>
          <div className="absolute top-20 left-20 w-72 h-72 bg-violet-500/20 rounded-full blur-[100px] animate-float"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* Video de presentación Autor/Creador (Vimeo) - a la misma altura del titular */}
          <div className="w-full lg:order-2">
            <div className="rounded-2xl overflow-hidden border border-violet-500/20 shadow-2xl shadow-violet-500/10 bg-black" data-testid="autor-video" style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
              <iframe
                src="https://player.vimeo.com/video/1229757959?badge=0&autopause=0&autoplay=1&muted=1&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Viubux Mentes Brillantes"
              />
            </div>
          </div>
          <div className="max-w-xl lg:order-1">
            <div className="animate-hero-text inline-flex items-center gap-2 px-4 py-2 bg-violet-500/10 border border-violet-500/20 rounded-full mb-6">
              <BookOpen className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-medium text-violet-300">Plan Autor Premium</span>
            </div>
            
            <h1 className="gotham-black text-4xl sm:text-5xl lg:text-6xl mb-6 text-white leading-tight">
              <span className="animate-hero-text delay-1 inline-block">Publica, Crece</span>
              <span className="block text-violet-400 animate-hero-text delay-2">y Monetiza</span>
            </h1>
            
            <p className="animate-hero-text delay-3 verdana-regular text-lg text-white/70 mb-8">
              El ecosistema completo para autores de mente brillante: distribuye tus obras, crea tu canal, 
              recibe patrocinio para eventos y accede a herramientas de marketing con IA.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => openAuthModal(true, 'author')}
                className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white text-base md:text-lg px-7 py-6 rounded-2xl font-semibold shadow-lg shadow-violet-500/25 transition-all duration-300 animate-pulse-glow text-left leading-snug h-auto whitespace-normal"
                data-testid="autor-cta-main"
              >
                Iniciar mi Registro como Autor en Viubux — sube tu ebook sin costo
                <ChevronRight className="w-5 h-5 ml-2 shrink-0" />
              </Button>
            </div>
          </div>
          </div>

          {/* 4 columnas: Publica · Comparte · Crece · Monetiza */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-14 md:mt-20">
            {[
              { icon: BookOpen, title: 'Publica', desc: 'Sube tu ebook, audiolibro o videolibro sin costo y forma parte del catálogo.' },
              { icon: Share2, title: 'Comparte', desc: 'Obtén tu enlace único y comparte tu contenido con tu comunidad.' },
              { icon: TrendingUp, title: 'Crece', desc: 'Amplía tu audiencia con marketing con IA y patrocinio de eventos.' },
              { icon: DollarSign, title: 'Monetiza', desc: 'Gana con venta directa, suscripción, el Pool 42% y el Rapid Bonus 25%.' },
            ].map((col, i) => {
              const Icon = col.icon;
              return (
                <AnimatedSection key={col.title} animation="scale-up" delay={i + 1}>
                  <div className="h-full bg-white/5 backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-violet-500/20 hover:border-violet-400/50 transition-all group" data-testid={`autor-step-${col.title.toLowerCase()}`}>
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg shadow-violet-500/30 group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="gotham-bold text-lg md:text-xl text-white mb-1">{i + 1}. {col.title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{col.desc}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <LatestContentCarousel theme="violet" title="Últimos contenidos publicados" subtitle="Explora lo más reciente que puedes distribuir y monetizar en Viubux" />

      {/* 10 Beneficios para Autores */}
      <section className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <h2 className="gotham-bold text-3xl md:text-4xl text-white mb-4">
              11 Beneficios Exclusivos para Autores
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Todo lo que necesitas para impulsar tu carrera como creador de contenido
            </p>
            <p className="text-white/70 max-w-3xl mx-auto mt-4 text-base md:text-lg">
              Tu contenido disponible para los usuarios <strong className="text-violet-300">24/7</strong> en modo
              Venta Directa y Suscripción, y monetiza a través del método
              <strong className="text-violet-300"> "recomendación natural"</strong> con nuestra tecnología integral.
            </p>
          </AnimatedSection>

          {/* Los 3 primeros beneficios DESTACADOS en grande */}
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-6">
            {/* Beneficio 1 - Destacado */}
            <AnimatedSection animation="scale-up" delay={1}>
              <div className="h-full bg-gradient-to-br from-violet-500/20 to-purple-500/10 backdrop-blur-sm rounded-2xl p-7 border border-violet-500/40 shadow-lg shadow-violet-500/10 hover:border-violet-400/60 transition-all group">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-5 shadow-lg shadow-violet-500/30 group-hover:scale-110 transition-transform">
                  <Share2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="gotham-bold text-xl mb-2 text-white">1. Obtén tu enlace Único y Sube tu PI</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Sube tu PI (<strong className="text-violet-300">hasta 2GB de Hosting</strong> para tus libros, ebooks, audiolibros,
                  podcast, curso, conferencias, booktrailer, etc.) y forma parte del Catálogo de Suscripción de Viubux Connect
                  y <strong className="text-violet-300">comienza a monetizar HOY mismo</strong>.
                </p>
              </div>
            </AnimatedSection>

            {/* Beneficio 2 - Destacado */}
            <AnimatedSection animation="scale-up" delay={2}>
              <div className="h-full bg-gradient-to-br from-amber-500/20 to-orange-500/10 backdrop-blur-sm rounded-2xl p-7 border border-amber-500/40 shadow-lg shadow-amber-500/10 hover:border-amber-400/60 transition-all group">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-5 shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h3 className="gotham-bold text-xl mb-2 text-white">2. Comunidad VIP + Pool 42%</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Únete a la comunidad VIP de VC y ten acceso inmediato al <strong className="text-amber-300">Pool de Autores del 42%</strong>
                  {' '}(adicional al <strong className="text-amber-300">25% del rapid bonus</strong>).
                </p>
              </div>
            </AnimatedSection>

            {/* Beneficio 3 - Destacado */}
            <AnimatedSection animation="scale-up" delay={3}>
              <div className="h-full bg-gradient-to-br from-emerald-500/20 to-teal-500/10 backdrop-blur-sm rounded-2xl p-7 border border-emerald-500/40 shadow-lg shadow-emerald-500/10 hover:border-emerald-400/60 transition-all group">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform">
                  <Percent className="w-8 h-8 text-white" />
                </div>
                <h3 className="gotham-bold text-xl mb-2 text-white">3. Hasta 50% en Co-Producciones</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Obtén hasta <strong className="text-emerald-300">50% de apoyo en Co-Producciones</strong> + un
                  {' '}<strong className="text-emerald-300">15% de Descuento</strong> en todos los servicios que Viubux Media ofrece para ti.
                </p>
              </div>
            </AnimatedSection>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Beneficio 4 */}
            <AnimatedSection delay={1}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group h-full">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <BookOpen className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-lg mb-1 text-white">4. Catálogo Viubux Connect</h3>
                    <p className="text-white/50 text-sm">Acceso al catálogo de Viubux Connect en Viubux Reader y Viubux Play</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Beneficio 5 */}
            <AnimatedSection delay={2}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group h-full">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500/20 to-pink-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 text-rose-400" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-lg mb-1 text-white">5. Tu Canal en Viubux Play</h3>
                    <p className="text-white/50 text-sm">Recibe soporte técnico para actualizarlo, subir tu contenido y personalizarlo</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Beneficio 6 */}
            <AnimatedSection delay={3}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group h-full">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Gift className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-lg mb-1 text-white">6. Descuentos Exclusivos</h3>
                    <p className="text-white/50 text-sm">En contenido de Alto Valor Transformacional y servicios ofrecidos por miembros de la comunidad</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Beneficio 7 */}
            <AnimatedSection delay={1}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group h-full">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Users2 className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-lg mb-1 text-white">7. Networking Privado</h3>
                    <p className="text-white/50 text-sm">Con Socios Estratégicos de la comunidad</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Beneficio 8 */}
            <AnimatedSection delay={2}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group h-full">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Video className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-lg mb-1 text-white">8. LiveStream + Eventos</h3>
                    <p className="text-white/50 text-sm">LiveStream + publica tus eventos ante la comunidad</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Beneficio 9 */}
            <AnimatedSection delay={3}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group h-full">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Rocket className="w-6 h-6 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-lg mb-1 text-white">9. Suite Pro-Marketing</h3>
                    <p className="text-white/50 text-sm">Acceso a la Suite de Herramientas tecnológicas: Pro-Marketing</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Beneficio 10 */}
            <AnimatedSection delay={1}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group h-full">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-rose-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Calendar className="w-6 h-6 text-pink-400" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-lg mb-1 text-white">10. 1 Evento Privado al Año</h3>
                    <p className="text-white/50 text-sm">Patrocinio de un Business Room o MiniForo (máx. 20 personas) con Audio y Video + grabación de tu ponencia en tu canal de Viubux Play</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Beneficio 11 */}
            <AnimatedSection delay={2}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-violet-500/30 transition-all group h-full">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Star className="w-6 h-6 text-fuchsia-400" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-lg mb-1 text-white">11. PhotoShoot + 3 Stories</h3>
                    <p className="text-white/50 text-sm">Para dar a conocer tus obras y tu canal de Viubux Play</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Imagen: escenario Viubux Connect (Evento Privado al Año) */}
          <AnimatedSection animation="scale-up" className="max-w-6xl mx-auto mt-8">
            <div className="relative rounded-2xl overflow-hidden border border-violet-500/20 shadow-2xl shadow-violet-500/10">
              <img
                src={IMAGES.autor.hero}
                alt="Evento privado en escenario con banners de Viubux Connect"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <p className="gotham-bold text-white text-xl md:text-2xl mb-1">Tu escenario en Viubux Connect</p>
                <p className="text-white/75 text-sm md:text-base max-w-xl">Comparte tu obra ante una audiencia real en tu Evento Privado anual (Business Room o MiniForo) con grabación en tu canal de Viubux Play.</p>
              </div>
            </div>
          </AnimatedSection>

          {/* Bonos y Beneficios adicionales para autores destacados */}
          <AnimatedSection animation="scale-up" className="max-w-6xl mx-auto mt-8">
            <div className="bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-transparent rounded-2xl p-7 md:p-8 border border-amber-500/30">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="gotham-bold text-xl md:text-2xl text-white">
                  Bonos y Beneficios adicionales para <span className="text-amber-300">autores destacados</span>
                </h3>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 bg-black/20 rounded-xl p-4">
                  <Headphones className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white/70 text-sm">Entrevistas en medios y podcasts (ADN40, Radio Disney, etc.)</p>
                </div>
                <div className="flex items-start gap-3 bg-black/20 rounded-xl p-4">
                  <Mic className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white/70 text-sm">Participa como Speaker en la convención anual de VC del siguiente año y tu libro destacado en el stand de la FIL de Guadalajara</p>
                </div>
                <div className="flex items-start gap-3 bg-black/20 rounded-xl p-4">
                  <Zap className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white/70 text-sm">Programa Piloto para tu Podcast o Curso</p>
                </div>
              </div>
              <p className="text-white/45 text-xs mt-5 italic">
                * Autor destacado es aquel que genera al menos <strong className="text-amber-300/80">20 suscriptores</strong> en su canal de Viubux Connect durante <strong className="text-amber-300/80">3 meses consecutivos</strong>.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Sección de imágenes - Producción y Eventos */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <AnimatedSection animation="from-left">
              <h2 className="gotham-bold text-3xl md:text-4xl text-white mb-6">
                Producción Profesional
              </h2>
              <p className="text-white/70 mb-6">
                Con tu membresía de autor, obtienes <strong className="text-violet-400">15% de descuento</strong> en todos los servicios de ViubuxMedia®:
              </p>
              <ul className="space-y-3 text-white/60">
                <li className="flex items-center gap-3">
                  <Mic className="w-5 h-5 text-violet-400" />
                  <span>Producción de audiolibros profesionales</span>
                </li>
                <li className="flex items-center gap-3">
                  <Video className="w-5 h-5 text-violet-400" />
                  <span>Booktrailers y videolibros</span>
                </li>
                <li className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-violet-400" />
                  <span>Servicios de edición y diseño editorial</span>
                </li>
                <li className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-violet-400" />
                  <span>Photoshoot profesional para autores</span>
                </li>
              </ul>
            </AnimatedSection>
            <AnimatedSection animation="from-right">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src={IMAGES.autor.studio}
                  alt="Estudio de producción"
                  className="rounded-2xl shadow-2xl shadow-violet-500/10 w-full h-48 object-cover"
                />
                <img 
                  src={IMAGES.autor.microphone}
                  alt="Micrófono profesional"
                  className="rounded-2xl shadow-2xl shadow-violet-500/10 w-full h-48 object-cover mt-8"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Sección Evento Anual */}
      <section className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <AnimatedSection animation="from-left" className="order-2 md:order-1">
              <img 
                src={IMAGES.autor.event}
                alt="Evento en vivo"
                className="rounded-2xl shadow-2xl shadow-violet-500/10 w-full h-80 object-cover"
              />
            </AnimatedSection>
            <AnimatedSection animation="from-right" className="order-1 md:order-2">
              <h2 className="gotham-bold text-3xl md:text-4xl text-white mb-6">
                Tu Evento Anual Patrocinado
              </h2>
              <p className="text-white/70 mb-6">
                ViubuxConnect te patrocina <strong className="text-violet-400">Foro, Audio y Video</strong> para organizar 
                tu evento al año: presentación de libro, conferencia, taller o curso.
              </p>
              <ul className="space-y-3 text-white/60">
                <li className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-violet-400" />
                  <span>Producido y grabado por ViubuxMedia</span>
                </li>
                <li className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-violet-400" />
                  <span>Invitación a toda la comunidad VC</span>
                </li>
                <li className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-violet-400" />
                  <span>Presencial o vía Zoom</span>
                </li>
                <li className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-violet-400" />
                  <span>Vende tickets, libros y merch sin comisión</span>
                </li>
              </ul>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Pricing Autor */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="scale-up" className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-violet-500/20 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/20 rounded-full mb-6">
                <Star className="w-4 h-4 text-violet-400" />
                <span className="text-sm font-medium text-violet-300">Membresía Autor Premium</span>
              </div>
              
              <h2 className="gotham-bold text-3xl md:text-4xl text-white mb-6">
                Inversión en tu Carrera
              </h2>
              
              <div className="flex items-baseline justify-center gap-2 mb-4">
                <span className="gotham-black text-6xl md:text-7xl text-white">$4,997</span>
                <span className="text-white/50 text-xl">MXN</span>
              </div>
              <div className="flex justify-center mb-2"><PriceRef mxn={4997} className="text-sm" /></div>
              <p className="text-white/50 text-lg mb-2">
                Inscripción + <strong className="text-violet-400">$199 MXN/mes</strong> de suscripción
              </p>
              <p className="text-white/40 text-sm mb-8">
                Los beneficios se renuevan anualmente. Si no deseas renovar, mantén tu suscripción activa como Audio-Lector.
              </p>

              <Button
                onClick={() => openAuthModal(true, 'author')}
                className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white text-xl px-12 py-7 rounded-full font-bold shadow-xl shadow-violet-500/30 transition-all duration-300 hover:scale-105 animate-pulse-glow"
                data-testid="autor-cta-pricing"
              >
                ¡Quiero Connect YA!
                <ChevronRight className="w-6 h-6 ml-2" />
              </Button>

              <p className="text-white/40 text-sm mt-6">
                10 beneficios exclusivos • Soporte prioritario • Comunidad VIP
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Sección de Videos YouTube - Autores */}
      <section className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-12">
            <h2 className="gotham-bold text-3xl md:text-4xl text-white mb-4">
              Conoce a Autores de la Comunidad
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Descubre las historias de éxito de autores que ya son parte de ViubuxConnect
            </p>
          </AnimatedSection>
          
          <AnimatedSection animation="scale-up">
            <YouTubeCarousel videos={YOUTUBE_VIDEOS.autor} theme="violet" />
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

const AuthModalContent = ({ onClose, initialReferralCode = '', initialShowRegister = false, initialUserType = 'consumer', applyDiscount = true }) => {
  const [isLogin, setIsLogin] = useState(!initialShowRegister);
  // Si el plan es 'free', usar consumer como user_type pero marcar membership_plan
  const effectiveUserType = initialUserType === 'free' ? 'consumer' : initialUserType;
  const membershipPlan = initialUserType === 'free' ? 'free' : 'premium';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    username: '',
    user_type: effectiveUserType,
    referral_code: initialReferralCode,
    apply_discount: applyDiscount,
    membership_plan: membershipPlan
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState(false); // Para detectar error de dominio
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [showEmailNotVerified, setShowEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [twoFAChallenge, setTwoFAChallenge] = useState(null); // {user_id, email}
  const [twoFAOtp, setTwoFAOtp] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const recaptchaRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Función para generar contraseña segura
  const generateSecurePassword = () => {
    const length = 12;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%&*_+-=';
    
    // Asegurar al menos uno de cada tipo
    let password = '';
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Rellenar el resto
    const allChars = uppercase + lowercase + numbers + symbols;
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Mezclar los caracteres
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    return password;
  };

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword();
    setGeneratedPassword(newPassword);
    setFormData(prev => ({
      ...prev,
      password: newPassword,
      confirmPassword: newPassword
    }));
    setShowPassword(true); // Mostrar contraseña para que el usuario la vea
    setCopiedPassword(false);
  };

  const handleCopyPassword = async () => {
    if (generatedPassword) {
      try {
        await navigator.clipboard.writeText(generatedPassword);
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
      } catch (err) {
        console.error('Error copying password:', err);
      }
    }
  };

  // Detectar si estamos en dominio de preview (CAPTCHA no funcionará)
  const isPreviewDomain = window.location.hostname.includes('preview.emergentagent.com') || 
                          window.location.hostname.includes('localhost');

  // Detectar error de CAPTCHA al cargar (dominio inválido)
  useEffect(() => {
    if (isPreviewDomain) {
      setCaptchaError(true); // Marcar error para omitir CAPTCHA en preview
    }
  }, [isPreviewDomain]);

  const handleResendVerification = async () => {
    const emailToResend = registeredEmail || formData.email;
    if (!emailToResend) return;
    
    setResendLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, { email: emailToResend });
      setResendSuccess(true);
    } catch (err) {
      setError('Error al reenviar el email de verificación');
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowEmailNotVerified(false);

    // Validar CAPTCHA en registro (solo si no hay error de dominio)
    if (!isLogin && !captchaValue && !captchaError && RECAPTCHA_SITE_KEY) {
      setError('Por favor completa el CAPTCHA');
      return;
    }

    // Validar que las contraseñas coincidan en registro
    if (!isLogin && formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    // Validar requisitos de contraseña segura
    if (!isLogin) {
      const password = formData.password;
      if (password.length < 8) {
        setError('La contraseña debe tener al menos 8 caracteres');
        return;
      }
      if (!/[A-Z]/.test(password)) {
        setError('La contraseña debe incluir al menos una mayúscula');
        return;
      }
      if (!/[a-z]/.test(password)) {
        setError('La contraseña debe incluir al menos una minúscula');
        return;
      }
      if (!/[0-9]/.test(password)) {
        setError('La contraseña debe incluir al menos un número');
        return;
      }
      if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
        setError('La contraseña debe incluir al menos un símbolo (!@#$%^&*...)');
        return;
      }
    }

    setLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      
      // Obtener el timestamp del código de referido (si existe)
      let referralTimestamp = null;
      if (!isLogin && formData.referral_code) {
        const storedReferral = localStorage.getItem('viubux_referral');
        if (storedReferral) {
          const parsed = JSON.parse(storedReferral);
          referralTimestamp = parsed.timestamp;
        }
      }
      
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            email: formData.email,
            password: formData.password,
            full_name: formData.full_name,
            username: formData.username,
            user_type: formData.user_type,
            referral_code: formData.referral_code,
            referral_timestamp: referralTimestamp,  // Timestamp de cuando se hizo click en el enlace
            apply_discount: formData.apply_discount,
            membership_plan: formData.membership_plan,  // 'free' o 'premium'
            captcha_token: captchaValue  // Enviar token de CAPTCHA
          };

      const response = await axios.post(`${BACKEND_URL}${endpoint}`, payload, {
        timeout: 15000 // 15 second timeout
      });
      
      // Verificar si es registro y requiere verificación de email
      if (!isLogin && response.data.requires_verification) {
        setRegistrationSuccess(true);
        setRegisteredEmail(response.data.email);
        // Limpiar el código de referido usado
        localStorage.removeItem('viubux_referral');
        return;
      }
      
      // Login requiere verificación en dos pasos (2FA por correo)
      if (isLogin && response.data.requires_2fa) {
        setTwoFAChallenge({ user_id: response.data.user_id, email: response.data.email });
        setTwoFAOtp('');
        return;
      }

      // Login exitoso
      login(response.data.token, response.data.user);
      // Si es registro directo (sin verificación), también limpiar el referral
      if (!isLogin) {
        localStorage.removeItem('viubux_referral');
      }
      onClose();
      navigate('/dashboard');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const statusCode = err.response?.status;
      
      // Manejar error de email no verificado
      if (errorDetail === 'email_not_verified') {
        setShowEmailNotVerified(true);
        setError('Tu email no está verificado. Revisa tu bandeja de entrada o solicita un nuevo enlace.');
      } 
      // Manejar errores de red/servidor (520, 502, 503, timeout, etc.)
      else if (!err.response || statusCode >= 500 || err.code === 'ECONNABORTED') {
        setError('🔄 ¡Que no cunda el pánico! El sistema se encuentra actualizando. Por favor re-intenta en 2 minutos.');
      }
      // Error de credenciales
      else if (statusCode === 401) {
        setError('Email o contraseña incorrectos');
      }
      // Otros errores
      else {
        setError(errorDetail || 'Error en la autenticación. Intenta de nuevo.');
      }
      
      // Reset CAPTCHA en caso de error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaValue(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    setCaptchaError(false);
  };

  // Handler para errores de CAPTCHA (dominio inválido, etc.)
  const handleCaptchaError = () => {
    console.warn('reCAPTCHA error: Invalid domain or configuration');
    setCaptchaError(true);
  };

  // Verificar código 2FA para completar el login
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(twoFAOtp)) {
      setError('Ingresa el código de 6 dígitos');
      return;
    }
    setTwoFALoading(true);
    setError('');
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/security/auth/2fa/verify`, {
        user_id: twoFAChallenge.user_id,
        otp: twoFAOtp
      });
      login(response.data.token, response.data.user);
      setTwoFAChallenge(null);
      onClose();
      navigate('/dashboard');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Código incorrecto');
    } finally {
      setTwoFALoading(false);
    }
  };

  // Pantalla de verificación en dos pasos
  if (twoFAChallenge) {
    return (
      <div data-testid="twofa-login-screen" className="text-center py-6">
        <div className="w-20 h-20 mx-auto mb-6 bg-violet-100 rounded-full flex items-center justify-center">
          <Lock className="h-10 w-10 text-violet-600" />
        </div>
        <h2 className="text-2xl font-bold mb-3" style={{ color: '#272b96' }}>Verificación en dos pasos</h2>
        <p className="text-gray-600 mb-1 text-sm">Enviamos un código de 6 dígitos a:</p>
        <p className="font-semibold mb-6" style={{ color: '#7012b2' }}>{twoFAChallenge.email}</p>
        <form onSubmit={handleVerify2FA} className="space-y-4">
          <input
            data-testid="twofa-login-otp-input"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={twoFAOtp}
            onChange={(e) => setTwoFAOtp(e.target.value.replace(/\D/g, ''))}
            placeholder="••••••"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-violet-500"
            autoFocus
          />
          {error && <p className="text-red-500 text-sm" data-testid="twofa-error">{error}</p>}
          <button
            data-testid="twofa-login-submit"
            type="submit"
            disabled={twoFALoading}
            className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold disabled:opacity-50"
          >
            {twoFALoading ? 'Verificando...' : 'Verificar e Iniciar Sesión'}
          </button>
          <button
            type="button"
            onClick={() => { setTwoFAChallenge(null); setError(''); }}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Volver
          </button>
        </form>
      </div>
    );
  }

  // Mostrar pantalla de éxito de registro
  if (registrationSuccess) {
    return (
      <div data-testid="registration-success" className="text-center py-8">
        <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
          <Mail className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold mb-4" style={{ color: '#272b96' }}>
          ¡Registro Exitoso!
        </h2>
        <p className="text-gray-600 mb-4">
          Hemos enviado un enlace de verificación a:
        </p>
        <p className="font-semibold text-lg mb-6" style={{ color: '#7012b2' }}>
          {registeredEmail}
        </p>
        <div className="bg-purple-50 p-4 rounded-lg mb-6">
          <p className="text-sm text-gray-700">
            Por favor revisa tu bandeja de entrada y haz clic en el enlace para activar tu cuenta.
            El enlace expira en 24 horas.
          </p>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          ¿No recibiste el correo? Revisa tu carpeta de spam o
        </p>
        {resendSuccess ? (
          <p className="text-green-600 text-sm">¡Email reenviado! Revisa tu bandeja.</p>
        ) : (
          <button
            onClick={handleResendVerification}
            disabled={resendLoading}
            className="text-purple-600 hover:text-purple-800 underline text-sm disabled:opacity-50"
          >
            {resendLoading ? 'Enviando...' : 'Reenviar enlace de verificación'}
          </button>
        )}
        <div className="mt-6 pt-6 border-t">
          <button
            onClick={() => {
              setRegistrationSuccess(false);
              setIsLogin(true);
            }}
            className="text-gray-600 hover:text-gray-800"
          >
            Ir a Iniciar Sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="auth-modal">
      {/* Tabs */}
      <div className="flex mb-6 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setIsLogin(true)}
          className={`flex-1 py-3 text-center font-semibold transition-colors ${
            isLogin 
              ? 'text-violet-600 border-b-2 border-violet-600' 
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          Iniciar Sesión
        </button>
        <button
          type="button"
          onClick={() => setIsLogin(false)}
          className={`flex-1 py-3 text-center font-semibold transition-colors ${
            !isLogin 
              ? 'text-violet-600 border-b-2 border-violet-600' 
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          Registrarse
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {!isLogin && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
              <input
                data-testid="input-full-name"
                type="text"
                placeholder="Juan Pérez"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 verdana-regular focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required={!isLogin}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de usuario</label>
              <input
                data-testid="input-username"
                type="text"
                placeholder="juanperez"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 verdana-regular focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all"
                value={formData.username || ''}
                onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                required={!isLogin}
                minLength={3}
                maxLength={30}
              />
              <p className="text-xs text-gray-400 mt-1">Tu enlace: connect.viubux.com?ref=<span className="text-violet-600">{formData.username || 'tu-usuario'}</span></p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de cuenta</label>
              <select
                data-testid="select-user-type"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 verdana-regular focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all bg-white"
                value={formData.user_type}
                onChange={(e) => setFormData({ ...formData, user_type: e.target.value })}
              >
                <option value="consumer">Audio-Lector</option>
                <option value="author">Autor/Creador</option>
              </select>
            </div>
          </>
        )}
        
        <div>
          {!isLogin && <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>}
          <input
            data-testid="input-email"
            type="email"
            placeholder="correo@ejemplo.com"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 verdana-regular focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>
        
        <div>
          {!isLogin && (
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Contraseña</label>
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium transition-colors"
                data-testid="generate-password-btn"
              >
                <Wand2 className="w-3 h-3" />
                Generar automática
              </button>
            </div>
          )}
          <div className="relative">
            <input
              data-testid="input-password"
              type={showPassword ? "text" : "password"}
              placeholder={isLogin ? "Contraseña" : "Mínimo 8 caracteres"}
              className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 verdana-regular focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all"
              value={formData.password}
              onChange={(e) => {
                setFormData({ ...formData, password: e.target.value });
                setGeneratedPassword(''); // Clear generated password when user types
              }}
              required
              minLength={8}
            />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-violet-600 transition-colors"
            data-testid="toggle-password-visibility"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
          </div>
          
          {/* Mensaje de contraseña generada con opción de copiar */}
          {!isLogin && generatedPassword && (
            <div className="mt-2 p-2 bg-violet-50 border border-violet-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-violet-600" />
                <span className="text-sm text-violet-700 font-mono">{generatedPassword}</span>
              </div>
              <button
                type="button"
                onClick={handleCopyPassword}
                className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-all ${
                  copiedPassword 
                    ? 'text-green-600 bg-green-100' 
                    : 'text-violet-600 hover:bg-violet-100'
                }`}
                data-testid="copy-password-btn"
              >
                {copiedPassword ? (
                  <>
                    <Check className="w-3 h-3" />
                    ¡Copiada!
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copiar
                  </>
                )}
              </button>
            </div>
          )}
          
          {!isLogin && !generatedPassword && (
            <p className="mt-1 text-xs text-gray-500">
              Debe incluir: mayúscula, minúscula, número y símbolo (!@#$%...)
            </p>
          )}
        </div>

        {!isLogin && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Contraseña</label>
            <div className="relative">
              <input
                data-testid="input-confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Repetir contraseña"
                className={`w-full px-4 py-3 pr-12 rounded-xl border verdana-regular focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all ${
                  formData.confirmPassword && formData.password !== formData.confirmPassword 
                    ? 'border-red-400 focus:border-red-400' 
                    : 'border-gray-200 focus:border-violet-500'
                }`}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-violet-600 transition-colors"
                data-testid="toggle-confirm-password-visibility"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
        )}

        {!isLogin && formData.confirmPassword && formData.password !== formData.confirmPassword && (
          <p className="text-red-500 text-xs verdana-regular -mt-2">Las contraseñas no coinciden</p>
        )}

        {!isLogin && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Código de Referido</label>
            <input
              data-testid="input-referral-code"
              type="text"
              placeholder="Opcional - ej: ABC123 o nombre_usuario"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 verdana-regular focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all"
              value={formData.referral_code}
              onChange={(e) => setFormData({ ...formData, referral_code: e.target.value })}
            />
          </div>
        )}

        {/* CAPTCHA para registro */}
        {!isLogin && RECAPTCHA_SITE_KEY && !captchaError && (
          <div className="flex justify-center" data-testid="recaptcha-container">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={handleCaptchaChange}
              onErrored={handleCaptchaError}
            />
          </div>
        )}

        {/* Mensaje cuando CAPTCHA tiene error de dominio */}
        {!isLogin && captchaError && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl p-3 text-center">
            Verificación de seguridad no disponible en este dominio. Puedes continuar con el registro.
          </div>
        )}

        {error && (
          <div data-testid="auth-error" className="bg-red-50 border border-red-200 text-red-600 text-sm verdana-regular text-center p-3 rounded-xl">
            {error}
          </div>
        )}

        {showEmailNotVerified && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <p className="text-sm text-amber-800 mb-2">
              Tu email aún no ha sido verificado.
            </p>
            {resendSuccess ? (
              <p className="text-green-600 text-sm">¡Enlace reenviado! Revisa tu bandeja de entrada.</p>
            ) : (
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="text-sm text-violet-600 hover:text-violet-700 underline disabled:opacity-50 transition-colors"
              >
                {resendLoading ? 'Enviando...' : 'Reenviar enlace de verificación'}
              </button>
            )}
          </div>
        )}

        <Button
          data-testid="submit-auth-form"
          type="submit"
          className="w-full bg-violet-600 hover:bg-violet-700 text-white py-6 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
          disabled={loading}
        >
          {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta')}
        </Button>
      </form>

      {isLogin && (
        <div className="mt-4 text-center">
          <button
            data-testid="forgot-password-link"
            onClick={() => {
              onClose();
              navigate('/forgot-password');
            }}
            className="verdana-regular text-sm text-violet-600 hover:text-violet-700 transition-colors"
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>
      )}
    </div>
  );
};

export default LandingPage;